import { Meter } from './Meter';
import { Panel, Row, Segmented, Select, Slider, Switch } from './ui';
import type { DeviceOption } from '../lib/devices';
import type { MeterLevels } from '../lib/audio';
import type { Settings } from '../settings';
import type { Store } from '../lib/store';

export function SourcesPanel({
  settings,
  update,
  devices,
  micStream,
  webcamStream,
  micMeterStore,
  onToggleMic,
  onToggleWebcam,
  onMicDevice,
  onWebcamDevice,
  onMicGain,
  onSystemGain,
  busy,
}: {
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  devices: { mics: DeviceOption[]; cams: DeviceOption[] };
  micStream: MediaStream | null;
  webcamStream: MediaStream | null;
  micMeterStore: Store<MeterLevels>;
  onToggleMic: (enabled: boolean) => void;
  onToggleWebcam: (enabled: boolean) => void;
  onMicDevice: (deviceId: string) => void;
  onWebcamDevice: (deviceId: string) => void;
  onMicGain: (value: number) => void;
  onSystemGain: (value: number) => void;
  busy: boolean;
}) {
  const micOptions = devices.mics.length
    ? devices.mics.map((device) => ({ value: device.deviceId, label: device.label }))
    : [{ value: '', label: 'System default' }];
  const camOptions = devices.cams.length
    ? devices.cams.map((device) => ({ value: device.deviceId, label: device.label }))
    : [{ value: '', label: 'System default' }];

  return (
    <Panel label="Sources">
      <Row label="Screen" hint="Chosen in the browser picker">
        <span className="chip chip--mono">system</span>
      </Row>

      <Row label="System audio" hint={settings.systemAudio ? 'Captured with the display' : 'Silent take'}>
        <Switch
          label="System audio"
          checked={settings.systemAudio}
          onChange={(value) => update({ systemAudio: value })}
          disabled={busy}
        />
      </Row>
      {settings.systemAudio ? (
        <Row label="System level">
          <Slider
            label="System audio level"
            min={0}
            max={2}
            step={0.05}
            value={settings.systemGain}
            onChange={onSystemGain}
            format={(value) => `${Math.round(value * 100)}%`}
          />
        </Row>
      ) : null}

      <Row label="Microphone" hint={micStream ? 'Live' : 'Off'}>
        <Switch
          label="Microphone"
          checked={settings.micEnabled}
          onChange={onToggleMic}
          disabled={busy}
        />
      </Row>
      {settings.micEnabled ? (
        <>
          <Row label="Input">
            <Select
              label="Microphone input"
              value={settings.micDeviceId}
              options={micOptions}
              onChange={onMicDevice}
            />
          </Row>
          <Row label="Level">
            <Slider
              label="Microphone level"
              min={0}
              max={3}
              step={0.05}
              value={settings.micGain}
              onChange={onMicGain}
              format={(value) => `${Math.round(value * 100)}%`}
            />
          </Row>
          <div className="meter-slot">
            <Meter store={micMeterStore} label="mic" level="mic" />
          </div>
        </>
      ) : null}

      <Row label="Camera inset" hint={webcamStream ? 'Composited into the frame' : 'Off'}>
        <Switch
          label="Camera inset"
          checked={settings.webcamEnabled}
          onChange={onToggleWebcam}
          disabled={busy}
        />
      </Row>
      {settings.webcamEnabled ? (
        <>
          <div className="cam-preview">
            <video
              ref={(node) => {
                if (node && webcamStream) node.srcObject = webcamStream;
              }}
              muted
              playsInline
              autoPlay
              style={{ transform: settings.overlay.mirror ? 'scaleX(-1)' : undefined }}
            />
            <span className="cam-preview__hint">inset preview</span>
          </div>
          <Row label="Camera">
            <Select
              label="Camera"
              value={settings.webcamDeviceId}
              options={camOptions}
              onChange={onWebcamDevice}
            />
          </Row>
          <Row label="Shape">
            <Segmented
              name="Inset shape"
              size="sm"
              value={settings.overlay.shape}
              onChange={(value) => update({ overlay: { ...settings.overlay, shape: value } })}
              options={[
                { value: 'rounded', label: 'Rounded' },
                { value: 'circle', label: 'Circle' },
              ]}
            />
          </Row>
          <Row label="Corner">
            <Segmented
              name="Inset corner"
              size="sm"
              value={settings.overlay.corner}
              onChange={(value) => update({ overlay: { ...settings.overlay, corner: value } })}
              options={[
                { value: 'tl', label: '↖' },
                { value: 'tr', label: '↗' },
                { value: 'bl', label: '↙' },
                { value: 'br', label: '↘' },
              ]}
            />
          </Row>
          <Row label="Size">
            <Slider
              label="Inset size"
              min={0.1}
              max={0.5}
              step={0.01}
              value={settings.overlay.scale}
              onChange={(value) => update({ overlay: { ...settings.overlay, scale: value } })}
              format={(value) => `${Math.round(value * 100)}%`}
            />
          </Row>
          <Row label="Mirror">
            <Switch
              label="Mirror camera"
              checked={settings.overlay.mirror}
              onChange={(value) => update({ overlay: { ...settings.overlay, mirror: value } })}
            />
          </Row>
        </>
      ) : null}
    </Panel>
  );
}
